import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCar,
  faCalendarCheck,
  faFileContract,
  faScrewdriverWrench,
  faChartLine,
  faBuilding,
  faChartSimple,
  faEye,
  faLayerGroup,
  faCarSide,
  faCheckCircle,
  faClock,
  faGlobe
} from '@fortawesome/free-solid-svg-icons'

const technologiesIcon = [
  "../images/react.png",
  "../images/azure.svg",
  "../images/azure-sql.png",
  "../images/nodejs.png",
  "../images/csharp.png",
];

const RentalSoftware = () => {
  return (
    <>
      <Helmet>
        <title>RD Rental Case Study | Rental Management Software by Data Repo</title>
        <meta
          name="description"
          content="Explore how Data Repo built RD Rental, a rental management system that helps rental businesses manage bookings, fleet tracking, billing, reporting, and multi-branch operations."
        />
      </Helmet>

      <section
        style={{
          backgroundImage: `url(../images/bg-casestudies-box.png)`,
          backgroundSize: "100% 80vh"
        }}
        className="relative flex flex-col justify-center w-full h-[65vh] bg-no-repeat bg-fixed pt-[65px] text-center text-white">
        <h1 className="text-3xl md:text-6xl font-bold uppercase">RD Rental</h1>
        <p className="mt-6 text-lg md:text-2xl max-w-3xl mx-auto">
          Rental Management Software Developed by Data Repo
        </p>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-6">Modernizing Rental Operations</h2>
            <p className="text-lg text-gray-700 mb-4">
              Rental businesses operate in a fast-moving environment where availability,
              coordination, and accuracy directly impact revenue. Many companies still rely on
              spreadsheets or disconnected tools, leading to booking conflicts and inefficiencies.
            </p>
            <p className="text-lg text-gray-700">
              RD Rental was developed by Data Repo to provide a centralized system that helps
              rental businesses manage bookings, vehicles, billing, and reporting from a single platform.
            </p>
          </div>
          <img src="../images/rd-rental-dashboard.png" alt="Rental dashboard overview" className="rounded-xl shadow-lg" />
        </div>
      </section>

      <section className="bg-white py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Inside the RD Rental Dashboard</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            RD Rental provides a clean, intuitive interface that allows staff to manage bookings,
            vehicles, customers, and reports from one centralized system.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <img
            src="../images/rd-rental-reservations-view.png"
            alt="RD Rental software dashboard showing booking overview and fleet status"
            className="rounded-xl shadow-lg"
          />

          <img
            src="../images/rd-rental-calendar.png"
            alt="Rental booking calendar interface displaying vehicle availability"
            className="rounded-xl shadow-lg"
          />
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-50 to-white py-8 px-6 md:px-20 border-y">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">

          <div className="flex items-center gap-4 justify-center md:justify-start">
            <div className="bg-blue-100 text-blue-700 p-3 rounded-full text-xl">
              <FontAwesomeIcon icon={faGlobe} />
            </div>
            <div>
              <p className="text-sm uppercase tracking-wide text-gray-500">Live Product</p>
              <p className="text-lg font-semibold text-gray-800">
                RD Rental is powering smarter rental operations
              </p>
            </div>
          </div>

          <a
            href="https://rdrental.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-blue-800 transition"
          >
            Visit Website →
          </a>
        </div>
      </section>

      <section className="bg-white py-20 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Project Objectives</h2>
          <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
            RD Rental was designed with a clear focus on improving operational control,
            visibility, and long-term scalability for modern rental businesses.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-gray-50 p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
              <div className="flex justify-center mb-5">
                <div className="bg-blue-100 text-blue-700 p-4 rounded-full text-2xl">
                  <FontAwesomeIcon icon={faLayerGroup} />
                </div>
              </div>
              <h3 className="font-semibold text-xl mb-3 text-center">Centralized Management</h3>
              <p className="text-gray-600 text-center">
                Consolidate bookings, vehicles, and customer data into one unified rental management platform.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
              <div className="flex justify-center mb-5">
                <div className="bg-blue-100 text-blue-700 p-4 rounded-full text-2xl">
                  <FontAwesomeIcon icon={faEye} />
                </div>
              </div>
              <h3 className="font-semibold text-xl mb-3 text-center">Operational Visibility</h3>
              <p className="text-gray-600 text-center">
                Enable real-time tracking of fleet availability, reservations, and branch-level activity.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
              <div className="flex justify-center mb-5">
                <div className="bg-blue-100 text-blue-700 p-4 rounded-full text-2xl">
                  <FontAwesomeIcon icon={faChartSimple} />
                </div>
              </div>
              <h3 className="font-semibold text-xl mb-3 text-center">Scalable Platform</h3>
              <p className="text-gray-600 text-center">
                Support business expansion across multiple locations without changing systems.
              </p>
            </div>

          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <img src="../images/manual-rental-process.jpg" alt="Manual rental process" className="rounded-xl shadow-md" />
          <div>
            <h2 className="text-3xl font-bold mb-6">Business Challenges</h2>
            <ul className="list-disc pl-5 text-lg text-gray-700 space-y-3">
              <li>Double bookings from manual scheduling</li>
              <li>No live view of vehicle availability</li>
              <li>Paper-based agreements and invoices</li>
              <li>Difficulty coordinating multiple branches</li>
              <li>Limited reporting on revenue and fleet use</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 px-6 md:px-20">
        <h2 className="text-3xl font-bold text-center mb-12">Core Software Modules</h2>

        <div className="grid md:grid-cols-3 gap-12 text-center">

          <div>
            <FontAwesomeIcon icon={faCar} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Fleet Management</h3>
            <p className="text-gray-700">
              Track vehicle availability, usage history, and status across rental locations.
            </p>
          </div>

          <div>
            <FontAwesomeIcon icon={faCalendarCheck} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Reservations</h3>
            <p className="text-gray-700">
              Manage bookings through a centralized calendar with availability validation.
            </p>
          </div>

          <div>
            <FontAwesomeIcon icon={faFileContract} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Digital Agreements</h3>
            <p className="text-gray-700">
              Store rental agreements digitally for quick access and better record keeping.
            </p>
          </div>

          <div>
            <FontAwesomeIcon icon={faScrewdriverWrench} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Maintenance Tracking</h3>
            <p className="text-gray-700">
              Maintain service logs and track upcoming vehicle maintenance schedules.
            </p>
          </div>

          <div>
            <FontAwesomeIcon icon={faChartLine} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Reporting & Insights</h3>
            <p className="text-gray-700">
              Generate reports on revenue, fleet utilization, and booking trends.
            </p>
          </div>

          <div>
            <FontAwesomeIcon icon={faBuilding} className="text-4xl text-blue-700 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Multi-Branch Management</h3>
            <p className="text-gray-700">
              Manage multiple rental locations from a single centralized system.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-gray-100 py-16 px-6 md:px-20">
        <div className="max-w-6xl mx-auto text-center mb-10">
          <h2 className="text-3xl font-bold mb-4">Key Modules in Action</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Each module in RD Rental is designed for clarity and efficiency,
            helping teams manage operations without complexity.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <img
            src="../images/rd-rental-vehicles.png"
            alt="Fleet management screen in RD Rental showing vehicle status and service logs"
            className="rounded-xl shadow-lg"
          />

          <img
            src="../images/rd-rental-maintenance.png"
            alt="Reservation management screen in rental software with booking details"
            className="rounded-xl shadow-lg"
          />

          <img
            src="../images/rd-rental-maintenance-rules.png"
            alt="Vehicle maintenance tracking interface with service history"
            className="rounded-xl shadow-lg"
          />

          <img
            src="../images/rd-rental-bookings.png"
            alt="Rental business reports dashboard with revenue and utilization insights"
            className="rounded-xl shadow-lg"
          />
        </div>
      </section>

      <section className="bg-gradient-to-b from-gray-100 to-white py-20 px-6 md:px-20 text-center">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-4">Business Impact</h2>
          <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
            After implementing RD Rental, rental businesses gained better control,
            improved operational clarity, and a more streamlined rental process.
          </p>

          <div className="grid md:grid-cols-3 gap-10">
            <div className="bg-white p-10 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
              <div className="flex justify-center mb-5">
                <div className="bg-green-100 text-green-600 p-4 rounded-full text-3xl">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">Higher</h3>
              <p className="font-semibold text-lg mb-2">Booking Accuracy</p>
              <p className="text-gray-600">
                Centralized scheduling reduced booking conflicts and manual errors.
              </p>
            </div>

            <div className="bg-white p-10 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
              <div className="flex justify-center mb-5">
                <div className="bg-blue-100 text-blue-600 p-4 rounded-full text-3xl">
                  <FontAwesomeIcon icon={faClock} />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">Faster</h3>
              <p className="font-semibold text-lg mb-2">Customer Check-In</p>
              <p className="text-gray-600">
                Digital records and organized workflows helped staff process rentals quicker.
              </p>
            </div>

            <div className="bg-white p-10 rounded-2xl shadow-md hover:shadow-xl transition duration-300">
              <div className="flex justify-center mb-5">
                <div className="bg-yellow-100 text-yellow-600 p-4 rounded-full text-3xl">
                  <FontAwesomeIcon icon={faCarSide} />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">Better</h3>
              <p className="font-semibold text-lg mb-2">Fleet Visibility</p>
              <p className="text-gray-600">
                Real-time vehicle tracking improved decision-making and fleet utilization.
              </p>
            </div>

          </div>
        </div>
      </section>

      <section className="flex flex-col items-center py-16 px-8">
        <h2 className="text-4xl font-bold mb-8">Technologies Used</h2>
        <div className="flex flex-wrap gap-8 justify-center">
          {technologiesIcon.map((item, index) => (
            <img key={index} src={item} alt="technology" width="90" />
          ))}
        </div>
      </section>

      <ContactUsComponent />
    </>
  )
}

export default RentalSoftware